import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtTime, fmtUptime, fileUrl } from "@/lib/api";
import { fitHints, FIT_SEV, FIT_CLS, FIT_LABEL } from "@/lib/mediafit";
import { useAuth } from "@/lib/auth";
import { StatusDot, PageHeader, CommandStatusChip } from "@/components/bits";
import SchedulesPanel from "@/components/SchedulesPanel";
import { RefreshCw, RotateCw, Camera, ArrowLeft, Upload, AlertTriangle, Film, Plus, UploadCloud, ChevronDown, ChevronUp, CheckCircle2, XCircle, Power, Wifi, Pencil } from "lucide-react";

const ACTIONS = [
  { type: "RELOAD", label: "Reload", icon: RefreshCw, testid: "device-remote-action-reload" },
  { type: "RESTART", label: "Neustart", icon: RotateCw, testid: "device-remote-action-restart" },
  { type: "REBOOT", label: "Reboot", icon: Power, testid: "device-remote-action-reboot" },
  { type: "SYNC", label: "Sync", icon: RefreshCw, testid: "device-remote-action-sync" },
  { type: "SCREENSHOT", label: "Screenshot", icon: Camera, testid: "device-remote-action-screenshot" },
];

export default function DeviceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState("");
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [resetArmed, setResetArmed] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [plDetail, setPlDetail] = useState(null);
  const [library, setLibrary] = useState([]);
  const [addMediaId, setAddMediaId] = useState("");
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiPsk, setWifiPsk] = useState("");
  const [wifiHidden, setWifiHidden] = useState(false);
  const [wifiArmed, setWifiArmed] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    api
      .get(`/devices/${id}`)
      .then((r) => {
        setData(r.data);
        const plId = r.data.active_playlist?.id || r.data.device.playlist_id;
        if (plId) {
          api.get(`/playlists/${plId}`).then((p) => setPlDetail(p.data)).catch(() => setPlDetail(null));
        } else {
          setPlDetail(null);
        }
      })
      .catch((e) => setError(fmtErr(e)));
    api.get("/media").then((r) => setLibrary(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    let alive = true;
    const run = () => alive && load();
    run();
    const t = setInterval(run, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [load]);

  async function sendCommand(type, payload) {
    setSending(type);
    try {
      await api.post(`/devices/${id}/commands`, { type, ...(payload ? { payload } : {}) });
      const labels = { RELOAD: "Neu laden", RESTART: "Neustart (App)", REBOOT: "Reboot (Gerät)", SYNC: "Sync", SCREENSHOT: "Screenshot", WIFI_SCAN: "WLAN-Scan", SET_WIFI: "WLAN-Wechsel" };
      toast.success(`Fernsteuerung befohlen: ${labels[type] || type} gesendet`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSending("");
    }
  }

  const [deleteArmed, setDeleteArmed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  async function saveRename() {
    const name = nameDraft.trim();
    if (!name) return;
    try {
      await api.patch(`/devices/${id}`, { display_name: name });
      toast.success("Bezeichnung geändert");
      setRenaming(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function deleteDevice() {
    try {
      await api.delete(`/devices/${id}`);
      toast.success("Gerät endgültig gelöscht — Credential widerrufen, Zustand und Verlauf entfernt");
      window.location.href = "/";
    } catch (e) {
      toast.error(fmtErr(e));
      setDeleteArmed(false);
    }
  }

  async function factoryReset() {
    try {
      await api.post(`/devices/${id}/commands`, { type: "FACTORY_RESET" });
      toast.success("Factory Reset befohlen — Gerät wird auf Werkseinstellungen zurückgesetzt");
      setResetArmed(false);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  function pickFiles() {
    fileInput.current?.click();
  }

  // Direkter Upload ohne Zwischen-Dialog: Fortschritt pro Datei, danach
  // landen die Medien am ANFANG der Playlist und das Gerät wird sofort gesynct.
  async function handleFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length || !data) return;
    const uploaded = [];
    for (const f of files) {
      const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((us) => [...us, { id: uid, name: f.name, progress: 0, status: "uploading" }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (data.device.tenant_id) fd.append("tenant_id", data.device.tenant_id);
        const { data: m } = await api.post("/media", fd, {
          onUploadProgress: (e) =>
            setUploads((us) =>
              us.map((x) => (x.id === uid ? { ...x, progress: e.total ? Math.round((e.loaded / e.total) * 100) : x.progress } : x))
            ),
        });
        uploaded.push(m);
        setUploads((us) => us.map((x) => (x.id === uid ? { ...x, progress: 100, status: "done" } : x)));
      } catch (e) {
        setUploads((us) => us.map((x) => (x.id === uid ? { ...x, status: "error", error: fmtErr(e) } : x)));
        toast.error(`${f.name}: ${fmtErr(e)}`);
      }
    }
    if (!uploaded.length) return;
    try {
      let plId = plDetail?.id || data.active_playlist?.id || data.device.playlist_id;
      let pl = plDetail;
      if (!plId) {
        const { data: created } = await api.post("/playlists", {
          name: `Display ${data.device.display_name || "Inhalte"}`,
          tenant_id: data.device.tenant_id,
        });
        await api.patch(`/devices/${id}`, { playlist_id: created.id });
        plId = created.id;
        pl = { ...created, items: [] };
      }
      if (!pl || pl.id !== plId) pl = (await api.get(`/playlists/${plId}`)).data;
      const items = [
        ...uploaded.map((m) => ({ media_id: m.id, duration_sec: 10 })),
        ...(pl.items || []).map((it) => ({ media_id: it.media_id, duration_sec: Number(it.duration_sec) || 10 })),
      ];
      await api.patch(`/playlists/${plId}`, { name: pl.name, items });
      await api.post(`/devices/${id}/commands`, { type: "SYNC" }).catch(() => {});
      toast.success(
        uploaded.length === 1
          ? `„${uploaded[0].filename}" hochgeladen — läuft ab jetzt auf dem Display`
          : `${uploaded.length} Medien hochgeladen — laufen ab jetzt auf dem Display`
      );
      setTimeout(() => setUploads([]), 2500);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function setPlaylist(playlistId) {
    try {
      await api.patch(`/devices/${id}`, { playlist_id: playlistId || null });
      toast.success("Wiedergabeliste zugewiesen");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function setRotation(rotation) {
    try {
      await api.patch(`/devices/${id}`, { rotation: Number(rotation) });
      toast.success(`Rotation gesetzt: ${rotation}°`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function removeItem(idx) {
    if (!plDetail) return;
    const items = plDetail.items
      .filter((_, i) => i !== idx)
      .map((it) => ({ media_id: it.media_id, duration_sec: Number(it.duration_sec) || 10 }));
    try {
      const { data: res } = await api.patch(`/playlists/${plDetail.id}`, { name: plDetail.name, items });
      toast.success(`Inhalt entfernt — Display synct automatisch (v${res.version})`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function setDuration(idx, dur) {
    if (!plDetail) return;
    const optimistic = plDetail.items.map((it, i) => (i === idx ? { ...it, duration_sec: dur } : it));
    setPlDetail({ ...plDetail, items: optimistic });
    clearTimeout(window.__durT);
    window.__durT = setTimeout(async () => {
      const items = optimistic.map((it) => ({ media_id: it.media_id, duration_sec: Number(it.duration_sec) || 10 }));
      try {
        const { data: res } = await api.patch(`/playlists/${plDetail.id}`, { name: plDetail.name, items });
        toast.success(`Laufzeit gespeichert (v${res.version})`);
        load();
      } catch (e) {
        toast.error(fmtErr(e));
      }
    }, 800);
  }

  async function addFromLibrary() {
    if (!addMediaId || !data) return;
    try {
      let plId = data.active_playlist?.id || data.device.playlist_id;
      if (!plId) {
        const { data: created } = await api.post("/playlists", {
          name: `Display ${data.device.display_name || "Inhalte"}`,
          tenant_id: data.device.tenant_id,
        });
        await api.patch(`/devices/${id}`, { playlist_id: created.id });
        plId = created.id;
        toast.success(`Playlist „${created.name}" angelegt und zugewiesen`);
      }
      const cur = plDetail && plDetail.id === plId ? plDetail : (await api.get(`/playlists/${plId}`)).data;
      const items = [
        ...(cur.items || []).map((it) => ({ media_id: it.media_id, duration_sec: Number(it.duration_sec) || 10 })),
        { media_id: addMediaId, duration_sec: 10 },
      ];
      const { data: res } = await api.patch(`/playlists/${plId}`, { name: cur.name, items });
      setAddMediaId("");
      toast.success(`Hinzugefügt — Display synct automatisch (v${res.version})`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  if (error) {
    return (
      <div className="panel p-8 text-center" data-testid="device-detail-error">
        <div className="font-mono text-sm text-red-400">{error}</div>
        <Link to="/" className="btn-ghost mt-4 inline-flex">Zum Flottenstatus</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="font-mono text-xs text-cyan-400 animate-pulse" data-testid="device-detail-loading">Lade Gerätedaten…</div>;
  }

  const { device, state, online, telemetry, screenshots, commands, playlists } = data;
  const latestShot = screenshots?.[0];
  const lastTelemetry = telemetry?.[0];

  return (
    <div data-testid="device-detail">
      <div className="mb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:text-cyan-300" data-testid="device-back-link">
          <ArrowLeft size={12} /> Geräteübersicht
        </Link>
      </div>
      <PageHeader
        title={
          renaming ? (
            <span className="inline-flex items-center gap-2">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="input-dark !text-lg !py-1 !w-64"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(false); }}
                data-testid="device-rename-input"
              />
              <button onClick={saveRename} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="device-rename-save">Speichern</button>
              <button onClick={() => setRenaming(false)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="device-rename-cancel">Abbrechen</button>
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              {device.display_name || device.label || "Gerät"}
              <button
                onClick={() => { setNameDraft(device.display_name || ""); setRenaming(true); }}
                className="text-slate-400 hover:text-cyan-300 transition-colors"
                title="Bezeichnung ändern"
                data-testid="device-rename-button"
              >
                <Pencil size={15} />
              </button>
            </span>
          )
        }
        sub={device.id}
      >
        {data.state?.screen_width ? (
          <span
            className="font-mono text-xs text-slate-100 border border-border rounded-sm px-2 py-1"
            data-testid="device-resolution-chip"
            title="Native Auflösung des angeschlossenen Displays — Plakate in dieser Größe erstellen"
          >
            {(device.rotation || 0) % 180 !== 0
              ? `${data.state.screen_height} × ${data.state.screen_width}`
              : `${data.state.screen_width} × ${data.state.screen_height}`}
          </span>
        ) : null}
        <StatusDot online={online} status={device.status} />
      </PageHeader>

      {/* Inhalte — der zentrale Bereich für Anwender */}
      <div
        className={`panel p-5 mb-6 transition-colors ${dragOver ? "!border-cyan-400/70 bg-cyan-500/5" : ""}`}
        data-testid="device-content-panel"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (device.status !== "SETUP") handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-display font-bold text-slate-100 text-lg">Inhalte auf diesem Display</div>
            <div className="text-xs text-slate-300 mt-0.5" data-testid="device-content-source">
              {plDetail ? (
                <>
                  Playlist „{plDetail.name}" · v{plDetail.version}
                  {data.schedule ? ` · läuft über Zeitplan „${data.schedule.name || "Zeitplan"}"` : ""}
                </>
              ) : (
                "Noch keine Inhalte"
              )}
            </div>
          </div>
          <button
            onClick={pickFiles}
            disabled={device.status === "SETUP"}
            className="btn-primary w-full sm:w-auto"
            data-testid="device-upload-cta"
          >
            <Upload size={15} /> Bilder / Videos hochladen
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,video/mp4"
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
            data-testid="device-file-input"
          />
        </div>

        {uploads.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="device-upload-progress">
            {uploads.map((u) => (
              <div key={u.id} className="flex items-center gap-3" data-testid={`upload-row-${u.id}`}>
                {u.status === "done" ? (
                  <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                ) : u.status === "error" ? (
                  <XCircle size={14} className="text-red-400 shrink-0" />
                ) : (
                  <RefreshCw size={14} className="text-cyan-400 animate-spin shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2 text-[11px]">
                    <span className="text-slate-300 truncate">{u.name}</span>
                    <span className="font-mono text-slate-300 shrink-0">
                      {u.status === "done" ? "fertig" : u.status === "error" ? "Fehler" : `${u.progress} %`}
                    </span>
                  </div>
                  <div className="h-1 mt-1 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-200 ${u.status === "error" ? "bg-red-500" : "bg-cyan-400"}`}
                      style={{ width: `${u.status === "done" ? 100 : u.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {plDetail && plDetail.items?.length ? (
          <div>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="device-content-strip">
              {plDetail.items.map((it, i) => { const fit = fitHints(it.media || {}, { sw: state.screen_width, sh: state.screen_height, rotation: device.rotation }).sort((a, b) => FIT_SEV[b.level] - FIT_SEV[a.level])[0]; return (
                <div
                  key={`${it.media_id}-${i}`}
                  className="shrink-0 w-36 border border-border rounded-sm bg-black/20 overflow-hidden"
                  data-testid={`device-content-item-${i}`}
                >
                  <div className="w-full h-20 bg-black flex items-center justify-center">
                    {it.media?.mime?.startsWith("video/") ? (
                      <Film size={18} className="text-cyan-400" />
                    ) : it.media?.url ? (
                      <img src={fileUrl(it.media.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : null}
                  </div>
                  <div className="p-2">
                    <div className="text-[11px] text-slate-300 truncate" title={it.media?.filename}>
                      {it.media?.filename || it.media_id}
                    </div>
                    {fit && (
                      <div className={`mt-1 px-1.5 py-1 rounded-sm border font-mono text-[9px] leading-snug ${FIT_CLS[fit.level]}`} data-testid={`device-content-fit-${i}`}>
                        {FIT_LABEL[fit.level]}: {fit.text}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1.5">
                      <input
                        type="number"
                        min="1"
                        max="3600"
                        value={it.duration_sec}
                        onChange={(e) => setDuration(i, e.target.value)}
                        className="input-dark !w-14 !py-0.5 !px-1.5 text-center font-mono text-[11px]"
                        data-testid={`device-content-duration-${i}`}
                      />
                      <span className="font-mono text-[9px] text-slate-400">sek</span>
                      <button
                        onClick={() => removeItem(i)}
                        className="ml-auto p-1 text-slate-400 hover:text-red-400 transition-colors"
                        data-testid={`device-content-remove-${i}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ); })}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <select
                value={addMediaId}
                onChange={(e) => setAddMediaId(e.target.value)}
                className="input-dark !w-64"
                data-testid="device-add-media-select"
              >
                <option value="">— Aus Mediathek hinzufügen —</option>
                {library.map((m) => (
                  <option key={m.id} value={m.id}>{m.filename}</option>
                ))}
              </select>
              <button onClick={addFromLibrary} disabled={!addMediaId} className="btn-ghost text-xs" data-testid="device-add-media-button">
                <Plus size={13} /> Hinzufügen
              </button>
              <span className="text-[11px] text-slate-400 ml-auto">
                Reihenfolge ändern: Mediathek → Wiedergabelisten
              </span>
            </div>
          </div>
        ) : (
          <button
            onClick={pickFiles}
            disabled={device.status === "SETUP"}
            className="w-full border border-dashed border-border hover:border-cyan-500/50 rounded-sm p-8 text-center transition-colors duration-150"
            data-testid="device-content-empty-cta"
          >
            <UploadCloud className="mx-auto text-cyan-400 mb-2" size={26} strokeWidth={1.5} />
            <div className="text-sm text-slate-300 font-medium">Bilder oder Videos hier ablegen</div>
            <div className="text-xs text-slate-300 mt-1 leading-relaxed">
              A4-/A0-Designs, Fotos oder Videos — nach dem Hochladen laufen sie automatisch auf diesem Display.
            </div>
          </button>
        )}

        {/* Zeitpläne — eingebettet in die Inhalte-Kachel */}
        <div className="mt-6 pt-5 border-t border-border/60">
          <SchedulesPanel tenantId={device.tenant_id} playlists={playlists} />
        </div>

        {/* Display-Ausrichtung — kompakte Segmented-Control am Panel-Ende */}
        <div className="mt-6 pt-5 border-t border-border/60 flex flex-wrap items-center gap-x-4 gap-y-3">
          <span className="mono-label shrink-0">Display-Ausrichtung</span>
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="device-rotation-select">
            {[0, 90, 180, 270].map((r, i) => (
              <button
                key={r}
                onClick={() => setRotation(r)}
                disabled={device.status === "SETUP"}
                className={`px-3.5 py-1.5 font-mono text-xs transition-colors duration-150 ${
                  i > 0 ? "border-l border-border" : ""
                } ${
                  device.rotation === r
                    ? "bg-cyan-500/15 text-cyan-300"
                    : "text-slate-300 hover:text-slate-300 hover:bg-slate-800/50"
                }`}
                data-testid={`device-rotation-${r}`}
              >
                {r}°
              </button>
            ))}
          </div>
          <span className="text-[11px] text-slate-400">Dreht den Bildschirminhalt — z. B. 90° für hochkant montierte Displays</span>
        </div>
      </div>

      {/* Erweitert: Technik-Bereich, standardmäßig eingeklappt */}
      <div className="panel" data-testid="device-advanced-panel">
        <button
          onClick={() => setAdvancedOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-800/30 transition-colors duration-150"
          data-testid="device-advanced-toggle"
        >
          <span className="mono-label">Erweitert — Fernsteuerung · Befehlsverlauf · Telemetrie · Identität · Gefahrenbereich</span>
          {advancedOpen ? <ChevronUp size={15} className="text-slate-300" /> : <ChevronDown size={15} className="text-slate-300" />}
        </button>
        {advancedOpen && (
          <div className="px-5 pb-5 pt-5 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="panel p-5" data-testid="device-screenshot-panel">
                <div className="flex items-center justify-between mb-3">
                  <span className="mono-label">Letzter Screenshot vom Gerät</span>
                  <button
                    onClick={() => sendCommand("SCREENSHOT")}
                    disabled={!!sending || device.status === "SETUP"}
                    className="btn-ghost !py-1 !px-2.5 text-[11px]"
                    data-testid="device-screenshot-refresh"
                  >
                    <Camera size={12} /> {sending === "SCREENSHOT" ? "Sende…" : "Aktualisieren"}
                  </button>
                </div>
                {latestShot ? (
                  <>
                    <img
                      src={fileUrl(latestShot.url)}
                      alt="Letzter Screenshot"
                      className="w-full rounded-sm border border-border bg-black aspect-video object-contain"
                      data-testid="device-last-screenshot"
                    />
                    <div className="font-mono text-[10px] text-slate-400 mt-2" data-testid="device-screenshot-time">
                      Stand: {fmtTime(latestShot.created_at)} — Diagnose-Ansicht, kein Live-Bild
                    </div>
                  </>
                ) : (
                  <div
                    className="w-full aspect-video rounded-sm border border-dashed border-border bg-black/40 flex items-center justify-center"
                    data-testid="device-no-screenshot"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      Noch kein Screenshot — oben auf „Aktualisieren" klicken
                    </span>
                  </div>
                )}
              </div>
              <div className="panel" data-testid="device-commands-panel">
            <div className="px-5 py-4 border-b border-border">
              <span className="mono-label">Befehlsverlauf</span>
            </div>
            {commands?.length ? (
              <table className="w-full text-sm" data-testid="device-commands-table">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="mono-label font-normal px-5 py-2.5">Befehl</th>
                    <th className="mono-label font-normal px-3 py-2.5">Status</th>
                    <th className="mono-label font-normal px-3 py-2.5">Ausgelöst von</th>
                    <th className="mono-label font-normal px-3 py-2.5">Zeit</th>
                  </tr>
                </thead>
                <tbody>
                  {commands.map((c) => (
                    <tr key={c.id} className="border-b border-border/50" data-testid={`command-row-${c.id}`}>
                      <td className="px-5 py-2.5 font-mono text-xs text-slate-200">{c.type}</td>
                      <td className="px-3 py-2.5"><CommandStatusChip status={c.status} /></td>
                      <td className="px-3 py-2.5 text-xs text-slate-300">{c.actor || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-300">{fmtTime(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-5 py-6 font-mono text-xs text-slate-400">Noch keine Befehle</div>
            )}
          </div>

          <div className="panel px-4 py-3" data-testid="device-telemetry-panel">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 font-mono text-[11px]">
              <span className="mono-label">Telemetrie</span>
              {[
                { label: "CPU", value: state.cpu_percent != null ? `${state.cpu_percent} %` : "—", testid: "telemetry-cpu" },
                { label: "RAM", value: state.mem_percent != null ? `${state.mem_percent} %` : "—", testid: "telemetry-ram" },
                { label: "Temp", value: state.temp_c != null ? `${state.temp_c} °C` : "—", testid: "telemetry-temp" },
                { label: "Uptime", value: fmtUptime(state.uptime_sec), testid: "telemetry-uptime" },
                { label: "Speicher", value: state.free_storage_mb != null ? `${state.free_storage_mb} MB` : "—", testid: "telemetry-storage" },
                { label: "Playlist v", value: state.playlist_version ?? "—", testid: "telemetry-playlist-version" },
                { label: "Bildrate", value: state.screen_refresh_hz ? `${state.screen_refresh_hz} Hz` : "—", testid: "telemetry-refresh", warn: state.screen_refresh_hz > 0 && state.screen_refresh_hz < 50 },
              ].map((t) => (
                <span key={t.label} data-testid={t.testid}>
                  <span className="text-slate-400">{t.label}</span>{" "}
                  <span className={t.warn ? "text-amber-400" : "text-cyan-300"}>{t.value}{t.warn ? " — 4K60/Kabel prüfen" : ""}</span>
                </span>
              ))}
              {lastTelemetry && (
                <span className="text-slate-400 ml-auto">Heartbeat {fmtTime(lastTelemetry.created_at)}</span>
              )}
            </div>
          </div>
            </div>

            <div className="space-y-6">
          <div className="panel p-5" data-testid="device-remote-actions">
            <div className="mono-label mb-4">Fernsteuerung</div>
            <div className="grid grid-cols-2 gap-2">
              {ACTIONS.map((a) => (
                <button
                  key={a.type}
                  onClick={() => sendCommand(a.type)}
                  disabled={!!sending || device.status === "SETUP"}
                  className="btn-ghost flex-col !gap-1 py-3 text-xs"
                  data-testid={a.testid}
                >
                  <a.icon size={16} className="text-cyan-400" />
                  {sending === a.type ? "Sende…" : a.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
              Whitelist-Befehle. Level-2-Bestätigung: Zustellung + Ausführungsstatus.
            </p>
          </div>

          <div className="panel p-5" data-testid="device-wifi-panel">
            <div className="mono-label mb-4 flex items-center gap-1.5"><Wifi size={12} /> WLAN am Gerät</div>
            <div className="text-xs text-slate-300 mb-4">
              Aktuell verbunden:{" "}
              <span className="font-mono text-cyan-300" data-testid="device-wifi-current">{state?.wifi_ssid || "—"}</span>
            </div>

            <button
              onClick={() => sendCommand("WIFI_SCAN")}
              disabled={!!sending || !online}
              className="btn-ghost w-full text-xs mb-3 disabled:opacity-40"
              title={online ? "Gerät scannt sichtbare WLANs (dauert ca. 15 s)" : "Gerät ist offline"}
              data-testid="device-wifi-scan"
            >
              {sending === "WIFI_SCAN" ? "Sende…" : "Netzwerke am Standort scannen"}
            </button>

            {state?.wifi_networks?.length > 0 && (
              <div className="mb-4" data-testid="device-wifi-networks">
                <div className="mono-label mb-1.5">
                  Gefunden · Scan {fmtTime(state.wifi_scan_at)}
                </div>
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {state.wifi_networks.map((n) => (
                    <button
                      key={n.ssid}
                      onClick={() => setWifiSsid(n.ssid)}
                      className={`w-full flex items-center justify-between px-3 py-1.5 rounded-sm border text-left transition-colors ${
                        wifiSsid === n.ssid ? "border-cyan-500/50 bg-cyan-500/5" : "border-border hover:bg-slate-800/40"
                      }`}
                      data-testid={`device-wifi-net-${n.ssid}`}
                    >
                      <span className="text-xs text-slate-200 truncate">{n.ssid}</span>
                      <span className="font-mono text-[10px] text-slate-400 shrink-0 ml-2">
                        {n.signal}% · {n.security || "offen"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <input
                value={wifiSsid}
                onChange={(e) => { setWifiSsid(e.target.value); setWifiArmed(false); }}
                placeholder="WLAN-Name (SSID)"
                className="input-dark"
                disabled={device.status === "SETUP"}
                data-testid="device-wifi-ssid-input"
              />
              <input
                value={wifiPsk}
                onChange={(e) => { setWifiPsk(e.target.value); setWifiArmed(false); }}
                type="password"
                placeholder="Passwort (leer = offenes Netz)"
                className="input-dark"
                disabled={device.status === "SETUP"}
                data-testid="device-wifi-psk-input"
              />
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wifiHidden}
                  onChange={(e) => setWifiHidden(e.target.checked)}
                  className="accent-cyan-400"
                  data-testid="device-wifi-hidden-check"
                />
                Verstecktes Netzwerk
              </label>
            </div>

            {wifiArmed ? (
              <div className="mt-3 pt-3 border-t border-amber-500/30" data-testid="device-wifi-armed">
                <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2 leading-relaxed">
                  Verbindung wechseln? Bei Misserfolg kehrt das Gerät automatisch ins alte WLAN zurück.
                </p>
                <div className="flex gap-1.5">
                  <button onClick={() => setWifiArmed(false)} className="btn-ghost !py-1.5 !px-3 text-xs flex-1" data-testid="device-wifi-cancel">
                    Abbrechen
                  </button>
                  <button
                    onClick={async () => {
                      await sendCommand("SET_WIFI", { ssid: wifiSsid.trim(), psk: wifiPsk, hidden: wifiHidden });
                      setWifiArmed(false);
                      setWifiPsk("");
                    }}
                    disabled={!!sending}
                    className="inline-flex flex-1 items-center justify-center rounded-sm bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400"
                    data-testid="device-wifi-confirm"
                  >
                    Jetzt wechseln
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setWifiArmed(true)}
                disabled={!wifiSsid.trim() || (wifiPsk.length > 0 && wifiPsk.length < 8) || device.status === "SETUP"}
                className="btn-ghost w-full mt-3 text-xs disabled:opacity-40"
                data-testid="device-wifi-apply"
              >
                WLAN am Gerät ändern
              </button>
            )}
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
              Das Gerät prüft nach dem Wechsel die Cloud-Erreichbarkeit. Klappt das nicht (z. B. falsches
              Passwort), stellt es die alte Verbindung automatisch wieder her und meldet den Fehler hier.
            </p>
          </div>

          <div className="panel p-5" data-testid="device-config-panel">
            <div className="mono-label mb-4">Standard-Wiedergabeliste</div>
            <div className="space-y-4">
              <div>
                <select
                  value={device.playlist_id || ""}
                  onChange={(e) => setPlaylist(e.target.value)}
                  className="input-dark"
                  disabled={device.status === "SETUP"}
                  data-testid="device-playlist-select"
                >
                  <option value="">— Keine —</option>
                  {playlists.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (v{p.version})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
                  Läuft immer dann, wenn kein Zeitplan greift.
                </p>
                {data.group && (
                  <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed" data-testid="device-group-hint">
                    In Gruppe{" "}
                    <Link to="/gruppen" className="text-cyan-400 hover:underline">
                      „{data.group.name}"
                    </Link>
                    {data.group.playlist_name ? ` (Gruppen-Playlist: „${data.group.playlist_name}")` : " (keine Gruppen-Playlist)"}
                    {" "}— die hier gesetzte Wiedergabeliste schlägt die Gruppe.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="panel p-5" data-testid="device-identity-panel">
            <div className="mono-label mb-3">Identität</div>
            <dl className="space-y-2 font-mono text-[11px]">
              <div>
                <dt className="text-slate-400">DEVICE_ID</dt>
                <dd className="text-slate-300 break-all">{device.id}</dd>
              </div>
              <div>
                <dt className="text-slate-400">INSTALLATION_ID</dt>
                <dd className="text-slate-300 break-all">{device.installation_id}</dd>
              </div>
              <div>
                <dt className="text-slate-400">REGISTRIERT</dt>
                <dd className="text-slate-300">{fmtTime(device.created_at)}</dd>
              </div>
              {device.paired_at && (
                <div>
                  <dt className="text-slate-400">GEKOPPELT</dt>
                  <dd className="text-slate-300">{fmtTime(device.paired_at)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="panel p-5 !border-red-500/30" data-testid="device-danger-panel">
            <div className="mono-label mb-3 flex items-center gap-2 !text-red-400">
              <AlertTriangle size={13} /> Gefahrenbereich
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed mb-3">
              Factory Reset: Der Pi löscht Identität, Medien-Cache und WLAN-Zugänge lokal und startet neu im
              Setup-Zustand (neue Einrichtung). Das Credential wird serverseitig widerrufen, der Verlauf bleibt erhalten.
            </p>
            {resetArmed ? (
              <div className="flex gap-2">
                <button onClick={() => setResetArmed(false)} className="btn-ghost flex-1 text-xs" data-testid="device-factory-reset-cancel">
                  Abbrechen
                </button>
                <button
                  onClick={factoryReset}
                  className="flex-1 inline-flex items-center justify-center rounded-sm bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-400 transition-colors"
                  data-testid="device-factory-reset-confirm"
                >
                  Endgültig zurücksetzen
                </button>
              </div>
            ) : (
              <button
                onClick={() => setResetArmed(true)}
                className="btn-ghost w-full !border-red-500/50 !text-red-400 hover:!bg-red-500/10 text-xs"
                data-testid="device-factory-reset-button"
              >
                Factory Reset
              </button>
            )}
            {user?.role === "SUPER_ADMIN" && (
              <>
                <p className="text-[11px] text-slate-300 leading-relaxed mt-4 mb-3">
                  Gerät löschen: Entfernt den Eintrag endgültig aus der Flotte — Credential, Zustand,
                  Telemetrie, Screenshots und Befehle werden gelöscht. Ein gekoppeltes Gerät im Feld
                  setzt sich danach selbst zurück (Onboarding).
                </p>
                {deleteArmed ? (
                  <div className="flex gap-2" data-testid="device-delete-armed">
                    <button onClick={() => setDeleteArmed(false)} className="btn-ghost flex-1 text-xs" data-testid="device-delete-cancel">
                      Abbrechen
                    </button>
                    <button
                      onClick={deleteDevice}
                      className="flex-1 inline-flex items-center justify-center rounded-sm bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-400 transition-colors"
                      data-testid="device-delete-confirm"
                    >
                      Endgültig löschen
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteArmed(true)}
                    className="btn-ghost w-full !border-red-500/50 !text-red-400 hover:!bg-red-500/10 text-xs"
                    data-testid="device-delete-button"
                  >
                    Gerät aus Flotte löschen
                  </button>
                )}
              </>
            )}
          </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
